import {
  CheckCircleOutlined,
  EditOutlined,
  KeyOutlined,
  LockOutlined,
  LoginOutlined,
  MailOutlined,
  MobileOutlined,
  SafetyCertificateOutlined,
  StopOutlined,
} from '@ant-design/icons';
import type { CountryCode } from 'libphonenumber-js';
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import type { components } from '../api/schema.gen';
import type { Dictionary } from '../lib/i18n/types';
import { ApiError, api, type OffsetPageInfo } from '../api/client';
import { GoogleIcon, MicrosoftIcon } from '../components/AuthIcons';
import { OtpInput } from '../components/OtpInput';
import { OtpResendPrompt } from '../components/OtpResendPrompt';
import { PasswordToggleInput } from '../components/PasswordToggleInput';
import { PasswordValidationRules } from '../components/PasswordValidationRules';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Modal } from '../components/Modal';
import { PageHeading } from '../components/PageHeading';
import { OffsetPager } from '../components/Pagination';
import { SelectMenu, type SelectMenuOption } from '../components/SelectMenu';
import { useToast } from '../components/Toast';
import { RecoveryCodesReveal, TwoFactorEnrollModal } from '../components/TwoFactorEnrollModal';
import { useLanguage } from '../lib/i18n/LanguageContext';
import { fadeUp } from '../lib/motionPresets';
import {
  DEFAULT_PHONE_COUNTRY,
  PHONE_COUNTRIES,
  formatNationalDisplay,
  isPhoneComplete,
  normalizeNationalInput,
  phonePlaceholderDisplay,
  toE164,
} from '../lib/phoneCountries';
import { formatDateTimeShort } from '../lib/dateFormat';
import { splitPhoneE164 } from '../lib/profileDraft';
import { useSession } from '../lib/SessionContext';
import { MIN_PASSWORD_LENGTH, isStrongPassword, normalizePasswordInput } from '../lib/password';
import { isOtpComplete } from '../lib/otp';

const DOB_YEAR_START = 1920;

// LLMR-76: kept small — each security-history category paginates on its own
// settings page, not a dedicated list screen, so a handful of rows per page
// is the point, not a limitation.
const SECURITY_HISTORY_PAGE_SIZE = 5;

/**
 * Maps a failed /me/phone/verify/start to something a customer can act on.
 *
 * Branching on `type` and status, never on `detail` — detail is prose the
 * server may reword. The three distinctions worth drawing here are: this is
 * your number's problem (fix the number), this is a pace problem (wait), or
 * this is our problem (nothing you can do).
 */
function phoneVerifyStartMessage(e: unknown, dict: Dictionary): string {
  if (!(e instanceof ApiError)) return dict.profile.errPhoneVerifyGeneric;
  if (e.status === 429) return dict.profile.errPhoneVerifyThrottled;
  if (e.status === 503) return dict.profile.errPhoneVerifyUnavailable;
  if (e.is('/problems/invalid-phone')) return dict.profile.errPhoneVerifyNotMobile;
  if (e.status === 409) {
    // One status, two very different situations — "already done" versus
    // "there is nothing to verify". `detail` is the only thing that separates
    // them today, so match loosely and fall back to the safer of the two.
    return /already/i.test(e.problem.detail ?? '')
      ? dict.profile.errPhoneVerifyAlreadyVerified
      : dict.profile.errPhoneVerifyNoPhone;
  }
  return dict.profile.errPhoneVerifyGeneric;
}

/** mm:ss for the expiry countdown. */
function formatCountdown(totalSeconds: number): string {
  const s = Math.max(0, totalSeconds);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

function parseIsoDob(iso: string): { day: string; month: string; year: string } {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return { day: '', month: '', year: '' };
  return {
    year: m[1] ?? '',
    month: String(Number(m[2])),
    day: String(Number(m[3])),
  };
}

/** Build YYYY-MM-DD, or '' if empty, or null if incomplete / invalid calendar date. */
function toIsoDob(day: string, month: string, year: string): string | null {
  if (!day && !month && !year) return '';
  if (!day || !month || !year) return null;
  const d = Number(day);
  const mo = Number(month);
  const y = Number(year);
  if (!Number.isFinite(d) || !Number.isFinite(mo) || !Number.isFinite(y)) return null;
  const dt = new Date(y, mo - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return null;
  return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function formatDobDisplay(day: string, month: string, year: string): string {
  if (!day || !month || !year) return '';
  return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
}

function initials(nameOrEmail: string): string {
  const base = (nameOrEmail.includes('@') ? nameOrEmail.split('@')[0] : nameOrEmail)?.trim() ?? '';
  const parts = base.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0] ?? '';
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  const second = parts[1] ?? '';
  return ((first[0] ?? '') + (second[0] ?? '')).toUpperCase() || '?';
}

type FieldKey = 'name' | 'username' | 'dob' | 'phone' | 'password';

function SettingsSection({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <section className="settings-section">
      <div className="settings-section-meta">
        <h2>{title}</h2>
        <p>{description}</p>
      </div>
      <div className="settings-section-body">{children}</div>
    </section>
  );
}

type SecurityHistoryCategoryKey = 'two_factor' | 'password' | 'recovery_code' | 'session';

/** Leading icon for a security-history row — keyed by audit action. */
function securityHistoryIcon(action: string): ReactNode {
  switch (action) {
    case 'auth.session.created':
      return <LoginOutlined aria-hidden="true" />;
    case 'auth.password_change.completed':
      return <LockOutlined aria-hidden="true" />;
    case 'auth.recovery_code.used':
      return <KeyOutlined aria-hidden="true" />;
    case 'auth.totp.enrolled':
      return <SafetyCertificateOutlined aria-hidden="true" />;
    case 'auth.sms.enrolled':
      return <MobileOutlined aria-hidden="true" />;
    case 'auth.email_otp.enrolled':
      return <MailOutlined aria-hidden="true" />;
    case 'auth.totp.disabled':
    case 'auth.sms.disabled':
    case 'auth.email_otp.disabled':
      return <StopOutlined aria-hidden="true" />;
    default:
      return <SafetyCertificateOutlined aria-hidden="true" />;
  }
}

/**
 * One flat, filterable list (LLMR-76) — owns its heading so the category
 * filter can sit on the same row. Changing the filter resets to page 1: a
 * page number from the unfiltered list means nothing once the underlying
 * row count changes.
 */
function SecurityHistoryList() {
  const { dict } = useLanguage();
  const [category, setCategory] = useState<SecurityHistoryCategoryKey | ''>('');
  const [events, setEvents] = useState<{ action: string; occurred_at: string }[]>([]);
  const [pageInfo, setPageInfo] = useState<OffsetPageInfo | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback((page: number, cat: SecurityHistoryCategoryKey | '') => {
    setBusy(true);
    api
      .securityHistory({ category: cat || undefined, page, page_size: SECURITY_HISTORY_PAGE_SIZE })
      .then((res) => {
        setEvents(res.events);
        setPageInfo(res.page);
      })
      .catch(() => {
        // Silent, same reasoning as loadTwoFactor: this section just stays
        // empty rather than surfacing a toast for a read the customer
        // hasn't asked for yet.
      })
      .finally(() => setBusy(false));
  }, []);

  useEffect(() => {
    load(1, category);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  const categoryOptions: SelectMenuOption<SecurityHistoryCategoryKey | ''>[] = [
    { value: '', label: dict.profile.securityHistoryFilterAll },
    { value: 'two_factor', label: dict.profile.securityHistoryCategoryTitle('two_factor') },
    { value: 'password', label: dict.profile.securityHistoryCategoryTitle('password') },
    { value: 'recovery_code', label: dict.profile.securityHistoryCategoryTitle('recovery_code') },
    { value: 'session', label: dict.profile.securityHistoryCategoryTitle('session') },
  ];

  return (
    <div className="security-history">
      <div className="security-history-header">
        <div className="security-history-heading">
          <h2>{dict.profile.securityHistoryHeading}</h2>
          <p>{dict.profile.securityHistoryDescription}</p>
        </div>
        <div className="security-history-filter">
          <SelectMenu
            id="security-history-category"
            aria-label={dict.profile.securityHistoryFilterLabel}
            value={category}
            onChange={(v) => setCategory(v)}
            options={categoryOptions}
          />
        </div>
      </div>
      {pageInfo?.total === 0 ? (
        <p className="note">{dict.profile.securityHistoryEmpty}</p>
      ) : (
        <>
          <ul className="security-history-list">
            {events.map((e, i) => (
              <li key={i}>
                <span className="security-history-icon" aria-hidden="true">
                  {securityHistoryIcon(e.action)}
                </span>
                <span className="security-history-event">{dict.profile.securityHistoryEventLabel(e.action)}</span>
                <span className="security-history-time">{formatDateTimeShort(e.occurred_at)}</span>
              </li>
            ))}
          </ul>
          <OffsetPager page={pageInfo} busy={busy} compact onPage={(p) => load(p, category)} />
        </>
      )}
    </div>
  );
}

function LockedField({
  label,
  htmlFor,
  locked,
  permanent,
  onUnlock,
  unlockLabel,
  error,
  hint,
  children,
}: {
  label?: string;
  htmlFor?: string;
  locked: boolean;
  permanent?: boolean;
  onUnlock?: () => void;
  unlockLabel: string;
  error?: string;
  hint?: string;
  children: ReactNode;
}) {
  const showLabel = Boolean(label);
  return (
    <div className={`profile-locked-field${locked ? ' is-locked' : ' is-editing'}${permanent ? ' is-permanent' : ''}`}>
      {(showLabel || (!permanent && locked && onUnlock)) && (
        <div className={`profile-locked-label-row${!showLabel ? ' profile-locked-label-row--end' : ''}`}>
          {showLabel &&
            (htmlFor ? <label htmlFor={htmlFor}>{label}</label> : <span className="profile-locked-label">{label}</span>)}
          {!permanent && locked && onUnlock && (
            <button
              type="button"
              className="profile-field-edit"
              aria-label={unlockLabel}
              title={unlockLabel}
              onClick={onUnlock}
            >
              <EditOutlined aria-hidden="true" />
            </button>
          )}
        </div>
      )}
      <div className="profile-locked-control">{children}</div>
      {hint && <p className="note">{hint}</p>}
      {error && <p className="field-error">{error}</p>}
    </div>
  );
}

/**
 * Profile settings — two-column sections (label left, fields right) matching
 * the portal's real data: personal details + password. Locked-by-default
 * fields unlock with the pencil; display name feeds the topbar. Profile
 * fields persist via PATCH /me; password via POST /me/password.
 */
export function EditProfile() {
  const { me, avatarUrl, updateProfile, refreshMe } = useSession();
  const { dict } = useLanguage();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const formId = useId();

  const savedUsername = me?.username?.trim() ?? '';
  const usernameLockedForever = savedUsername.length > 0;
  const savedPhone = splitPhoneE164(me?.phone_e164);
  const initialDob = parseIsoDob(me?.date_of_birth ?? '');

  const [displayName, setDisplayName] = useState(me?.display_name ?? '');
  const [username, setUsername] = useState(savedUsername);
  const [dobDay, setDobDay] = useState(initialDob.day);
  const [dobMonth, setDobMonth] = useState(initialDob.month);
  const [dobYear, setDobYear] = useState(initialDob.year);
  const [phoneCountry, setPhoneCountry] = useState<CountryCode>(savedPhone.phone_country);
  const [phoneNational, setPhoneNational] = useState(savedPhone.phone_national);
  /** Local preview while editing; null means "use server avatarUrl". */
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarCleared, setAvatarCleared] = useState(false);
  const [avatarDirty, setAvatarDirty] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [unlocked, setUnlocked] = useState<Record<FieldKey, boolean>>({
    name: false,
    username: false,
    dob: false,
    phone: false,
    password: false,
  });

  const [fieldErrors, setFieldErrors] = useState<{
    name?: string;
    username?: string;
    dob?: string;
    phone?: string;
    avatar?: string;
    currentPassword?: string;
    newPassword?: string;
    confirmPassword?: string;
  }>({});
  const [busy, setBusy] = useState(false);

  const wantsPassword =
    unlocked.password && Boolean(currentPassword || newPassword || confirmPassword);
  const newPasswordStrong = isStrongPassword(newPassword);
  const newPasswordInvalid = newPassword !== '' && !newPasswordStrong;
  const passwordChangeValid =
    !wantsPassword ||
    (Boolean(currentPassword) &&
      newPasswordStrong &&
      newPassword !== currentPassword &&
      confirmPassword === newPassword);

  type TwoFactorMethods = {
    available: components['schemas']['TwoFactorMethod'][];
    enrolled: components['schemas']['TwoFactorMethod'][];
    enforced: boolean;
  };
  const [twoFactor, setTwoFactor] = useState<TwoFactorMethods | null>(null);
  /** SMS only: confirm destination before enroll sends an OTP. */
  const [confirmingSmsEnroll, setConfirmingSmsEnroll] = useState(false);
  const [enrollingMethod, setEnrollingMethod] = useState<components['schemas']['TwoFactorMethod'] | null>(null);
  const [disablingMethod, setDisablingMethod] = useState<components['schemas']['TwoFactorMethod'] | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  // Bumped to force every category section (each owns its own page state) to
  // reset to page 1 and refetch — used after a 2FA disable, so the newly
  // logged event is visible without a manual refresh.
  const [securityHistoryRefreshKey, setSecurityHistoryRefreshKey] = useState(0);

  const [sendingCode, setSendingCode] = useState(false);
  const [phoneVerifyError, setPhoneVerifyError] = useState<string | null>(null);
  const [phoneVerifySession, setPhoneVerifySession] = useState<{
    codeId: string;
    phoneMasked: string;
    expiresInSeconds: number;
  } | null>(null);

  /**
   * Requests a code for the phone already saved on the account.
   *
   * The send happens here rather than inside the modal — unlike TOTP
   * enrolment, where opening the modal IS the start and a failure can be
   * rendered inside it. This one spends real credit on an SMS, so it must be
   * possible for it to fail before any modal appears: a customer who is
   * throttled, or whose number turns out to be a landline, should see why on
   * the row they clicked, not in a dialog that has nothing else to show.
   */
  async function startPhoneVerification() {
    setSendingCode(true);
    setPhoneVerifyError(null);
    try {
      const res = await api.startPhoneVerification();
      setPhoneVerifySession({
        codeId: res.code_id,
        phoneMasked: res.phone_masked,
        expiresInSeconds: res.expires_in_seconds,
      });
    } catch (e) {
      setPhoneVerifyError(phoneVerifyStartMessage(e, dict));
    } finally {
      setSendingCode(false);
    }
  }

  function loadTwoFactor() {
    api
      .twoFactorMethods()
      .then(setTwoFactor)
      .catch(() => {
        // Silent: the section below just stays hidden. Nothing else on this
        // page depends on it, and a fetch failure here is not worth a toast
        // for a section the customer hasn't asked to open yet.
      });
  }

  useEffect(() => {
    loadTwoFactor();
  }, []);

  useEffect(() => {
    const phone = splitPhoneE164(me?.phone_e164);
    const nextDob = parseIsoDob(me?.date_of_birth ?? '');
    setDisplayName(me?.display_name ?? '');
    setUsername(me?.username?.trim() ?? '');
    setDobDay(nextDob.day);
    setDobMonth(nextDob.month);
    setDobYear(nextDob.year);
    setPhoneCountry(phone.phone_country);
    setPhoneNational(phone.phone_national);
    setAvatarPreview(null);
    setAvatarCleared(false);
    setAvatarDirty(false);
  }, [me]);

  const email = me?.email ?? '';
  const shownAvatar = avatarCleared ? null : (avatarPreview ?? avatarUrl);
  const previewLabel = displayName.trim() || email;
  const headerName = displayName.trim() || email;

  const smsConfirmPhoneDisplay = (() => {
    const e164 = me?.phone_e164?.trim() ?? '';
    if (!e164) return e164;
    const split = splitPhoneE164(e164);
    const country = PHONE_COUNTRIES.find((c) => c.iso2 === split.phone_country) ?? DEFAULT_PHONE_COUNTRY;
    const national = formatNationalDisplay(split.phone_country, split.phone_national);
    return national ? `${country.dialCode} ${national}` : e164;
  })();
  const dobYears = Array.from({ length: new Date().getFullYear() - DOB_YEAR_START + 1 }, (_, i) =>
    String(new Date().getFullYear() - i),
  );

  function unlock(key: FieldKey) {
    setUnlocked((u) => ({ ...u, [key]: true }));
  }

  function onPickAvatar(file: File | undefined) {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setFieldErrors((e) => ({ ...e, avatar: dict.profile.errAvatarType }));
      return;
    }
    if (file.size > 512 * 1024) {
      setFieldErrors((e) => ({ ...e, avatar: dict.profile.errAvatarSize }));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : null;
      setAvatarPreview(result);
      setAvatarCleared(false);
      setAvatarDirty(true);
      setFieldErrors((e) => ({ ...e, avatar: undefined }));
    };
    reader.readAsDataURL(file);
  }

  function clearAvatar() {
    setAvatarPreview(null);
    setAvatarCleared(true);
    setAvatarDirty(true);
    setFieldErrors((e) => ({ ...e, avatar: undefined }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  /**
   * Unlocking a field is optional — never require the customer to finish an
   * edit they opened and abandoned. Only reject values that are present and
   * invalid (bad username shape, incomplete phone, future DOB).
   */
  function validateProfile(): boolean {
    const next: typeof fieldErrors = {};

    if (!usernameLockedForever) {
      const u = username.trim();
      if (u && !/^[a-zA-Z0-9._-]{3,32}$/.test(u)) next.username = dict.profile.errUsername;
    }

    if (dobDay || dobMonth || dobYear) {
      const iso = toIsoDob(dobDay, dobMonth, dobYear);
      if (iso === null) next.dob = dict.profile.errDob;
      else if (iso) {
        const d = new Date(`${iso}T00:00:00`);
        if (d > new Date()) next.dob = dict.profile.errDob;
      }
    }

    if (phoneNational.trim() && !isPhoneComplete(phoneCountry, phoneNational)) {
      next.phone = dict.profile.errPhone;
    }

    setFieldErrors((prev) => ({
      ...prev,
      name: undefined,
      username: next.username,
      dob: next.dob,
      phone: next.phone,
      currentPassword: undefined,
      newPassword: undefined,
      confirmPassword: undefined,
    }));
    return Object.keys(next).length === 0;
  }

  function validatePassword(): boolean {
    const next: typeof fieldErrors = {};
    const normalizedCurrent = normalizePasswordInput(currentPassword);
    const normalizedNew = normalizePasswordInput(newPassword);
    const normalizedConfirm = normalizePasswordInput(confirmPassword);
    if (!normalizedCurrent) next.currentPassword = dict.profile.errCurrentPassword;
    if (!isStrongPassword(normalizedNew)) next.newPassword = dict.profile.errNewPassword;
    else if (normalizedNew === normalizedCurrent) next.newPassword = dict.profile.errPasswordSame;
    if (normalizedConfirm !== normalizedNew) next.confirmPassword = dict.profile.errPasswordMismatch;
    setFieldErrors((prev) => ({ ...prev, ...next }));
    return Object.keys(next).length === 0;
  }

  function isProfileDirty(): boolean {
    const savedName = (me?.display_name ?? '').trim();
    const savedDob = me?.date_of_birth ?? '';
    const currentDob = toIsoDob(dobDay, dobMonth, dobYear) ?? '';
    const savedE164 = (me?.phone_e164 ?? '').trim();
    const currentE164 =
      phoneNational.trim() === ''
        ? ''
        : isPhoneComplete(phoneCountry, phoneNational)
          ? (toE164(phoneCountry, phoneNational) ?? '')
          : '__invalid__';

    if (displayName.trim() !== savedName) return true;
    if ((currentDob || '') !== savedDob) return true;
    if (currentE164 !== savedE164) return true;
    if (avatarDirty) return true;
    if (!usernameLockedForever && unlocked.username && username.trim() && username.trim() !== savedUsername) {
      return true;
    }
    return false;
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!validateProfile()) return;

    const wantsPassword = unlocked.password && (currentPassword || newPassword || confirmPassword);
    if (wantsPassword && !validatePassword()) return;

    const profileDirty = isProfileDirty();
    if (!profileDirty && !wantsPassword) {
      setUnlocked({ name: false, username: false, dob: false, phone: false, password: false });
      return;
    }

    setBusy(true);
    try {
      if (profileDirty) {
        const phoneE164 =
          phoneNational.trim() === ''
            ? null
            : isPhoneComplete(phoneCountry, phoneNational)
              ? toE164(phoneCountry, phoneNational)
              : null;

        const patch: Parameters<typeof updateProfile>[0] = {
          display_name: displayName.trim() || null,
          date_of_birth: toIsoDob(dobDay, dobMonth, dobYear) || null,
        };
        if (phoneNational.trim() === '') {
          patch.phone_e164 = null;
        } else if (phoneE164) {
          patch.phone_e164 = phoneE164;
        }
        if (!usernameLockedForever && unlocked.username && username.trim()) {
          patch.username = username.trim();
        }
        if (avatarDirty) {
          patch.avatar_data_url = avatarCleared ? null : avatarPreview;
        }

        try {
          await updateProfile(patch);
          setAvatarPreview(null);
          setAvatarCleared(false);
          setAvatarDirty(false);
          if (!wantsPassword) showToast(dict.profile.saved);
        } catch (err) {
          if (err instanceof ApiError && err.is('/problems/username-taken')) {
            setFieldErrors((fe) => ({ ...fe, username: dict.profile.errUsernameTaken }));
          } else if (err instanceof ApiError && err.is('/problems/username-immutable')) {
            setFieldErrors((fe) => ({ ...fe, username: dict.profile.errUsernameImmutable }));
          } else if (err instanceof ApiError && err.is('/problems/phone-taken')) {
            setFieldErrors((fe) => ({ ...fe, phone: dict.profile.errPhoneTaken }));
          } else if (err instanceof ApiError && err.is('/problems/invalid-phone')) {
            setFieldErrors((fe) => ({ ...fe, phone: dict.profile.errPhone }));
          } else {
            setFieldErrors((fe) => ({ ...fe, name: dict.profile.errProfileGeneric }));
          }
          return;
        }
      }

      if (wantsPassword) {
        try {
          await api.changePassword(
            normalizePasswordInput(currentPassword),
            normalizePasswordInput(newPassword),
          );
          setCurrentPassword('');
          setNewPassword('');
          setConfirmPassword('');
          setUnlocked((u) => ({ ...u, password: false }));
          showToast(dict.profile.passwordSaved);
        } catch (err) {
          if (err instanceof ApiError && err.is('/problems/wrong-password')) {
            setFieldErrors((fe) => ({ ...fe, currentPassword: dict.profile.errWrongPassword }));
          } else if (err instanceof ApiError && err.is('/problems/weak-password')) {
            setFieldErrors((fe) => ({
              ...fe,
              newPassword: err.problem.detail ?? dict.profile.errNewPassword,
            }));
          } else if (err instanceof ApiError && err.status === 403) {
            setFieldErrors((fe) => ({ ...fe, currentPassword: dict.profile.errNoPassword }));
          } else if (err instanceof ApiError && err.status === 429) {
            setFieldErrors((fe) => ({ ...fe, currentPassword: dict.profile.errPasswordRateLimit }));
          } else {
            setFieldErrors((fe) => ({ ...fe, currentPassword: dict.profile.errPasswordGeneric }));
          }
          if (profileDirty) showToast(dict.profile.saved);
          return;
        }
      }

      setUnlocked({ name: false, username: false, dob: false, phone: false, password: false });
    } finally {
      setBusy(false);
    }
  }

  function cancel() {
    const phone = splitPhoneE164(me?.phone_e164);
    const nextDob = parseIsoDob(me?.date_of_birth ?? '');
    setDisplayName(me?.display_name ?? '');
    setUsername(me?.username?.trim() ?? '');
    setDobDay(nextDob.day);
    setDobMonth(nextDob.month);
    setDobYear(nextDob.year);
    setPhoneCountry(phone.phone_country);
    setPhoneNational(phone.phone_national);
    setAvatarPreview(null);
    setAvatarCleared(false);
    setAvatarDirty(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setFieldErrors({});
    setUnlocked({ name: false, username: false, dob: false, phone: false, password: false });
    navigate(-1);
  }

  return (
    <div className="page">
      <PageHeading subtitle={dict.profile.subtitle}>{dict.profile.title}</PageHeading>

      <motion.form
        variants={fadeUp}
        initial="hidden"
        animate="show"
        className="panel profile-edit"
        onSubmit={(e) => void submit(e)}
        noValidate
      >
        <header className="profile-edit-header">
          <button
            type="button"
            className="profile-edit-avatar-wrap"
            aria-label={dict.profile.uploadPhoto}
            title={dict.profile.uploadPhoto}
            onClick={() => fileInputRef.current?.click()}
          >
            {shownAvatar ? (
              <img className="profile-edit-avatar" src={shownAvatar} alt="" />
            ) : (
              <span className="profile-edit-avatar profile-edit-avatar--initials" aria-hidden="true">
                {initials(previewLabel)}
              </span>
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => {
              onPickAvatar(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
          <div className="profile-edit-header-text">
            <div className="profile-edit-header-name">{headerName}</div>
            <div className="profile-edit-photo-actions">
              <button type="button" className="profile-photo-upload" onClick={() => fileInputRef.current?.click()}>
                {dict.profile.uploadPhoto}
              </button>
              {shownAvatar && (
                <button type="button" className="profile-photo-delete" onClick={clearAvatar}>
                  {dict.profile.deletePhoto}
                </button>
              )}
            </div>
            {fieldErrors.avatar && <p className="field-error">{fieldErrors.avatar}</p>}
          </div>
        </header>

        <SettingsSection title={dict.profile.personalHeading} description={dict.profile.personalDescription}>
          <div className="settings-fields">
            <LockedField
              label={dict.profile.nameLabel}
              htmlFor={`${formId}-name`}
              locked={!unlocked.name}
              onUnlock={() => unlock('name')}
              unlockLabel={dict.profile.unlockField}
              error={fieldErrors.name}
            >
              <input
                id={`${formId}-name`}
                value={displayName}
                autoComplete="name"
                readOnly={!unlocked.name}
                onChange={(e) => setDisplayName(e.target.value)}
                aria-invalid={Boolean(fieldErrors.name)}
              />
            </LockedField>

            <LockedField
              label={dict.profile.usernameLabel}
              htmlFor={`${formId}-username`}
              locked={usernameLockedForever || !unlocked.username}
              permanent={usernameLockedForever}
              onUnlock={usernameLockedForever ? undefined : () => unlock('username')}
              unlockLabel={dict.profile.unlockField}
              hint={usernameLockedForever ? dict.profile.usernameLockedHint : dict.profile.usernameHint}
              error={fieldErrors.username}
            >
              <input
                id={`${formId}-username`}
                value={username}
                autoComplete="username"
                readOnly={usernameLockedForever || !unlocked.username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder={dict.profile.usernamePlaceholder}
                aria-invalid={Boolean(fieldErrors.username)}
              />
            </LockedField>

            <LockedField
              label={dict.profile.emailLabel}
              htmlFor={`${formId}-email`}
              locked
              permanent
              unlockLabel={dict.profile.unlockField}
              hint={dict.profile.emailHint}
            >
              <input id={`${formId}-email`} value={email} readOnly disabled autoComplete="email" />
            </LockedField>

            {(me?.linked_providers ?? []).length > 0 && (
              <LockedField
                label={dict.profile.linkedProvidersLabel}
                locked
                permanent
                unlockLabel=""
              >
                <div className="linked-provider-badge-row">
                  {(me?.linked_providers ?? []).map((provider) => (
                    <span key={provider} className="linked-provider-badge">
                      {provider === 'google' && <GoogleIcon />}
                      {provider === 'microsoft' && <MicrosoftIcon />}
                      <span>{dict.profile.linkedProviderBadge(provider)}</span>
                    </span>
                  ))}
                </div>
              </LockedField>
            )}

            <LockedField
              label={dict.profile.dobLabel}
              htmlFor={`${formId}-dob-day`}
              locked={!unlocked.dob}
              onUnlock={() => unlock('dob')}
              unlockLabel={dict.profile.unlockField}
              error={fieldErrors.dob}
            >
              {unlocked.dob ? (
                <div className="dob-row">
                  <SelectMenu
                    id={`${formId}-dob-day`}
                    aria-label={dict.profile.dobDayLabel}
                    value={dobDay}
                    onChange={setDobDay}
                    aria-invalid={Boolean(fieldErrors.dob)}
                    options={[
                      { value: '', label: dict.profile.dobDayLabel },
                      ...Array.from({ length: 31 }, (_, i) => String(i + 1)).map((d) => ({
                        value: d,
                        label: d,
                      })),
                    ]}
                  />
                  <SelectMenu
                    aria-label={dict.profile.dobMonthLabel}
                    value={dobMonth}
                    onChange={setDobMonth}
                    aria-invalid={Boolean(fieldErrors.dob)}
                    options={[
                      { value: '', label: dict.profile.dobMonthLabel },
                      ...Array.from({ length: 12 }, (_, i) => String(i + 1)).map((m) => ({
                        value: m,
                        label: m,
                      })),
                    ]}
                  />
                  <SelectMenu
                    aria-label={dict.profile.dobYearLabel}
                    value={dobYear}
                    onChange={setDobYear}
                    aria-invalid={Boolean(fieldErrors.dob)}
                    options={[
                      { value: '', label: dict.profile.dobYearLabel },
                      ...dobYears.map((y) => ({ value: y, label: y })),
                    ]}
                  />
                </div>
              ) : (
                <input
                  id={`${formId}-dob-day`}
                  value={formatDobDisplay(dobDay, dobMonth, dobYear)}
                  readOnly
                  placeholder="DD/MM/YYYY"
                />
              )}
            </LockedField>

            <LockedField
              label={dict.profile.phoneLabel}
              htmlFor={`${formId}-phone`}
              locked={!unlocked.phone}
              onUnlock={() => unlock('phone')}
              unlockLabel={dict.profile.unlockField}
              error={fieldErrors.phone}
            >
              <div className="phone-row">
                <SelectMenu
                  aria-label={dict.profile.phoneCountryLabel}
                  value={phoneCountry}
                  disabled={!unlocked.phone}
                  className="phone-country-select"
                  onChange={(next) => {
                    setPhoneCountry(next);
                    setPhoneNational((prev) => normalizeNationalInput(next, prev));
                  }}
                  options={PHONE_COUNTRIES.map((c) => ({ value: c.iso2, label: c.label }))}
                />
                <input
                  id={`${formId}-phone`}
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel-national"
                  readOnly={!unlocked.phone}
                  placeholder={phonePlaceholderDisplay(phoneCountry)}
                  value={formatNationalDisplay(phoneCountry, phoneNational)}
                  onChange={(e) => setPhoneNational(normalizeNationalInput(phoneCountry, e.target.value))}
                  className="phone-national-input"
                  aria-invalid={Boolean(fieldErrors.phone)}
                />
              </div>
            </LockedField>

            {/*
              Verification status for the SAVED number, deliberately not the one
              being typed. phone_country is only ever set for a number that was
              messaged and confirmed, and editing the field clears it server-side
              — so while the form is dirty this row describes what is on the
              account, which is also what SMS 2FA eligibility follows.
            */}
            {me?.phone_e164 && (
              <div className="phone-verify-row">
                {me.phone_country ? (
                  <p className="note note-positive">
                    <CheckCircleOutlined aria-hidden="true" /> {dict.profile.phoneVerifyBadgeVerified}
                  </p>
                ) : (
                  <>
                    <p className="note">{dict.profile.phoneVerifyWhyNote}</p>
                    <button
                      type="button"
                      className="ghost"
                      disabled={sendingCode}
                      onClick={() => void startPhoneVerification()}
                    >
                      {sendingCode ? dict.profile.phoneVerifySending : dict.profile.phoneVerifyStartButton}
                    </button>
                  </>
                )}
                {phoneVerifyError && <p className="field-error">{phoneVerifyError}</p>}
              </div>
            )}
          </div>
        </SettingsSection>

        {(me?.has_password ?? true) && (
        <SettingsSection title={dict.profile.securityHeading} description={dict.profile.passwordDescription}>
          {!unlocked.password ? (
            <div className="settings-password-locked">
              <p className="note">{dict.profile.passwordLockedHint}</p>
              <button type="button" className="ghost" onClick={() => unlock('password')}>
                <EditOutlined aria-hidden="true" />
                {dict.profile.changePassword}
              </button>
            </div>
          ) : (
            <div className="settings-fields">
              <div className="profile-edit-field">
                <label htmlFor={`${formId}-current-pw`}>{dict.profile.currentPasswordLabel}</label>
                <PasswordToggleInput
                  id={`${formId}-current-pw`}
                  value={currentPassword}
                  autoComplete="current-password"
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  aria-invalid={Boolean(fieldErrors.currentPassword)}
                  show={showCurrent}
                  onToggleShow={() => setShowCurrent((v) => !v)}
                  showLabel={dict.profile.showPassword}
                  hideLabel={dict.profile.hidePassword}
                />
                {fieldErrors.currentPassword && <p className="field-error">{fieldErrors.currentPassword}</p>}
              </div>
              <div className="profile-edit-field">
                <label htmlFor={`${formId}-new-pw`}>{dict.profile.newPasswordLabel}</label>
                <PasswordToggleInput
                  id={`${formId}-new-pw`}
                  value={newPassword}
                  autoComplete="new-password"
                  onChange={(e) => setNewPassword(e.target.value)}
                  className={newPasswordInvalid ? 'password-invalid' : undefined}
                  minLength={MIN_PASSWORD_LENGTH}
                  aria-invalid={newPasswordInvalid || Boolean(fieldErrors.newPassword)}
                  aria-describedby={newPassword !== '' ? `${formId}-new-pw-rules` : undefined}
                  show={showNew}
                  onToggleShow={() => setShowNew((v) => !v)}
                  showLabel={dict.profile.showPassword}
                  hideLabel={dict.profile.hidePassword}
                />
                <PasswordValidationRules password={newPassword} id={`${formId}-new-pw-rules`} />
                {fieldErrors.newPassword && <p className="field-error">{fieldErrors.newPassword}</p>}
              </div>
              <div className="profile-edit-field">
                <label htmlFor={`${formId}-confirm-pw`}>{dict.profile.confirmPasswordLabel}</label>
                <PasswordToggleInput
                  id={`${formId}-confirm-pw`}
                  value={confirmPassword}
                  autoComplete="new-password"
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  aria-invalid={Boolean(fieldErrors.confirmPassword)}
                  show={showConfirm}
                  onToggleShow={() => setShowConfirm((v) => !v)}
                  showLabel={dict.profile.showPassword}
                  hideLabel={dict.profile.hidePassword}
                />
                {fieldErrors.confirmPassword && <p className="field-error">{fieldErrors.confirmPassword}</p>}
              </div>
            </div>
          )}
        </SettingsSection>
        )}

        {twoFactor && (
          <SettingsSection title={dict.profile.twoFactorHeading} description={dict.profile.twoFactorDescription}>
            {twoFactor.enforced && twoFactor.enrolled.length === 0 && (
              <p className="note">{dict.profile.twoFactorEnforcedNote}</p>
            )}
            {twoFactor.available.map((method) => {
              const enrolled = twoFactor.enrolled.includes(method);
              const label = dict.profile.methodName(method);
              return (
                <div className="settings-password-locked" key={method}>
                  <p className={enrolled ? 'note note-positive' : 'note'}>
                    {enrolled
                      ? dict.profile.twoFactorEnrolledStatusFor(label)
                      : dict.profile.twoFactorNotEnrolledStatusFor(label)}
                  </p>
                  {!enrolled && (
                    <button
                      type="button"
                      className="ghost"
                      onClick={() => {
                        if (method === 'sms') {
                          setConfirmingSmsEnroll(true);
                          return;
                        }
                        setEnrollingMethod(method);
                      }}
                    >
                      <EditOutlined aria-hidden="true" />
                      {dict.profile.twoFactorSetupButtonFor(label)}
                    </button>
                  )}
                  {enrolled && (
                    <button type="button" className="ghost" onClick={() => setDisablingMethod(method)}>
                      {dict.profile.twoFactorDisableButtonFor(label)}
                    </button>
                  )}
                </div>
              );
            })}
          </SettingsSection>
        )}

        <section className="settings-section settings-section--stack">
          <SecurityHistoryList key={securityHistoryRefreshKey} />
        </section>

        <div className="profile-edit-actions">
          <button type="button" className="ghost" onClick={cancel} disabled={busy}>
            {dict.common.cancel}
          </button>
          <button type="submit" disabled={busy || !passwordChangeValid}>
            {busy ? dict.common.working : dict.profile.save}
          </button>
        </div>
      </motion.form>

      <AnimatePresence>
        {phoneVerifySession && (
          <PhoneVerifyModal
            codeId={phoneVerifySession.codeId}
            phoneMasked={phoneVerifySession.phoneMasked}
            expiresInSeconds={phoneVerifySession.expiresInSeconds}
            onClose={() => setPhoneVerifySession(null)}
            onVerified={() => {
              setPhoneVerifySession(null);
              showToast(dict.profile.phoneVerifyDone);
              // Both, and in this order: refreshMe flips the row to verified,
              // loadTwoFactor is what makes `sms` appear in the section below
              // — the whole point of having verified at all.
              void refreshMe();
              loadTwoFactor();
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {confirmingSmsEnroll && (
          <ConfirmDialog
            title={dict.profile.twoFactorSmsConfirmTitle}
            body={<p>{dict.profile.twoFactorSmsConfirmBody(smsConfirmPhoneDisplay)}</p>}
            confirmLabel={dict.profile.twoFactorConfirmButton}
            onConfirm={() => {
              setConfirmingSmsEnroll(false);
              setEnrollingMethod('sms');
            }}
            onCancel={() => setConfirmingSmsEnroll(false)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {enrollingMethod && (
          <TwoFactorEnrollModal
            method={enrollingMethod}
            onClose={() => setEnrollingMethod(null)}
            onConfirmed={(codes) => {
              setEnrollingMethod(null);
              setRecoveryCodes(codes);
              loadTwoFactor();
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {recoveryCodes && (
          <RecoveryCodesReveal codes={recoveryCodes} onDismiss={() => setRecoveryCodes(null)} />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {disablingMethod && (
          <TwoFactorDisableModal
            method={disablingMethod}
            onClose={() => setDisablingMethod(null)}
            onDisabled={() => {
              setDisablingMethod(null);
              showToast(dict.profile.twoFactorDisableDone);
              loadTwoFactor();
              setSecurityHistoryRefreshKey((k) => k + 1);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

/**
 * Removes an already-confirmed second factor (LLMR-76), after re-proving
 * control of that SAME method — the current TOTP code, or a fresh code this
 * modal sends on mount for sms/email_otp. Structurally the mirror image of
 * TwoFactorEnrollModal: totp shows a bare code input with no send step
 * (nothing to send — the code already lives in the app), sms/email_otp send
 * immediately on open, same as enrollment's own OTP path.
 */
function TwoFactorDisableModal({
  method,
  onClose,
  onDisabled,
}: {
  method: components['schemas']['TwoFactorMethod'];
  onClose: () => void;
  onDisabled: () => void;
}) {
  const { dict } = useLanguage();
  const isOTP = method !== 'totp';
  const [destinationMasked, setDestinationMasked] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [sendBusy, setSendBusy] = useState(!isOTP ? false : true);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [startError, setStartError] = useState<string | null>(null);

  async function start(isResend: boolean) {
    setSendBusy(true);
    setStartError(null);
    if (isResend) setResendMessage(null);
    try {
      const res = await api.sendTwoFactorDisableCode(method);
      setDestinationMasked(res.destination_masked);
      if (isResend) setResendMessage(dict.profile.twoFactorResendSent);
    } catch {
      setStartError(dict.profile.errTwoFactorDisableSendFailed);
    } finally {
      setSendBusy(false);
    }
  }

  useEffect(() => {
    if (isOTP) void start(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function confirm(e: React.FormEvent) {
    e.preventDefault();
    if (code.trim() === '') return;
    setBusy(true);
    setError(null);
    try {
      await api.disableTwoFactor(method, code.trim());
      onDisabled();
    } catch (e) {
      setError(e instanceof ApiError && e.status === 401 ? dict.profile.errTwoFactorDisableCode : dict.profile.errTwoFactorDisableGeneric);
    } finally {
      setBusy(false);
    }
  }

  const label = dict.profile.methodName(method);

  return (
    <Modal title={dict.profile.twoFactorDisableModalTitleFor(label)} onClose={onClose}>
      <form onSubmit={(e) => void confirm(e)}>
        <p className="note note-warning">{dict.profile.twoFactorDisableWarning}</p>

        {method === 'totp' ? (
          <p className="note">{dict.profile.twoFactorDisableTotpLede}</p>
        ) : (
          <p className="note">
            {sendBusy && !destinationMasked
              ? dict.profile.twoFactorSendingCode
              : destinationMasked
                ? dict.profile.twoFactorDisableOtpLede(destinationMasked)
                : dict.profile.twoFactorSendingCode}
          </p>
        )}

        <label id="totp-disable-code-label" htmlFor="totp-disable-code">
          {dict.profile.twoFactorDisableCodeLabel}
        </label>
        <OtpInput
          id="totp-disable-code"
          value={code}
          onChange={setCode}
          autoFocus
          aria-invalid={Boolean(error)}
          aria-labelledby="totp-disable-code-label"
          digitAriaLabel={dict.common.otpDigitLabel}
        />
        {error && <p className="field-error">{error}</p>}
        {isOTP && startError && <p className="field-error">{startError}</p>}

        {isOTP && (
          <OtpResendPrompt
            prompt={dict.common.otpDidntGetCode}
            actionLabel={dict.common.otpResendLink}
            busyLabel={dict.profile.twoFactorSendingCode}
            busy={sendBusy}
            sentMessage={resendMessage}
            onResend={() => void start(true)}
          />
        )}

        <div className="modal-actions">
          <button type="button" className="ghost" onClick={onClose} disabled={busy}>
            {dict.common.cancel}
          </button>
          <button type="submit" disabled={busy || !isOtpComplete(code)}>
            {busy ? dict.profile.twoFactorDisableConfirming : dict.profile.twoFactorDisableConfirmButton}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Collects the code sent to the customer's phone.
 *
 * Unlike TwoFactorEnrollModal, this one does NOT start anything on mount —
 * the code was already sent by the row that opened it, precisely so a send
 * failure never becomes an empty dialog. Everything here is about the code
 * already in flight: which number it went to, how long it has left, and one
 * chance per submission to type it correctly.
 *
 * `codeId` is threaded through rather than resolved server-side. Two starts
 * racing would otherwise leave confirm checking whichever row happened to be
 * newest, which is exactly the bug the TOTP path shipped with once.
 */
function PhoneVerifyModal({
  codeId,
  phoneMasked,
  expiresInSeconds,
  onClose,
  onVerified,
}: {
  codeId: string;
  phoneMasked: string;
  expiresInSeconds: number;
  onClose: () => void;
  onVerified: () => void;
}) {
  const { dict } = useLanguage();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [remaining, setRemaining] = useState(expiresInSeconds);

  useEffect(() => {
    if (remaining <= 0) return;
    const t = setTimeout(() => setRemaining((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [remaining]);

  const expired = remaining <= 0;

  async function confirm(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = code.trim();
    if (trimmed === '') return;
    setBusy(true);
    setError(null);
    try {
      await api.confirmPhoneVerification(codeId, trimmed);
      onVerified();
    } catch (err) {
      // 400 covers wrong, expired, already used and out-of-attempts as one
      // undifferentiated case — the server deliberately does not distinguish
      // them, so neither does this.
      setError(
        err instanceof ApiError && (err.status === 400 || err.status === 409)
          ? dict.profile.errPhoneVerifyCode
          : dict.profile.errPhoneVerifyGeneric,
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={dict.profile.phoneVerifyModalTitle} onClose={onClose}>
      <form onSubmit={(e) => void confirm(e)}>
        <p className="note">{dict.profile.phoneVerifySentTo.replace('{phone}', phoneMasked)}</p>

        <label id="phone-verify-code-label" htmlFor="phone-verify-code">
          {dict.profile.phoneVerifyCodeLabel}
        </label>
        <OtpInput
          id="phone-verify-code"
          value={code}
          onChange={setCode}
          autoFocus
          disabled={expired}
          aria-invalid={Boolean(error)}
          aria-labelledby="phone-verify-code-label"
          digitAriaLabel={dict.common.otpDigitLabel}
        />

        {expired ? (
          <p className="field-error">{dict.profile.phoneVerifyExpired}</p>
        ) : (
          <p className="note">
            {dict.profile.phoneVerifyExpiresIn.replace('{time}', formatCountdown(remaining))}
          </p>
        )}
        {error && <p className="field-error">{error}</p>}

        <div className="modal-actions">
          <button type="button" className="ghost" onClick={onClose} disabled={busy}>
            {dict.common.cancel}
          </button>
          <button type="submit" disabled={busy || expired || !isOtpComplete(code)}>
            {busy ? dict.profile.phoneVerifyConfirming : dict.profile.phoneVerifyConfirmButton}
          </button>
        </div>
      </form>
    </Modal>
  );
}
